import React from 'react';

// -----------------------------------------------------------------------------
// Types & Interfaces
// -----------------------------------------------------------------------------

type BadgeVariant = 'solid' | 'soft' | 'outline';
type BadgeIntent = 'free' | 'core' | 'pro' | 'neutral' | 'dev';

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  intent?: BadgeIntent;
  variant?: BadgeVariant;
  label?: string;
  showIcon?: boolean;
  animate?: boolean; // For the "pulse" effect on dev badges
}

// -----------------------------------------------------------------------------
// Icons
// -----------------------------------------------------------------------------

const ProIcon = () => (
  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
  </svg>
);

const ClockIcon = () => (
  <svg className="w-3 h-3 mr-1 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
  </svg>
);

const PulseDot = () => (
  <span className="relative flex h-2 w-2 mr-1.5">
    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1B365D]"></span>
  </span>
);

// -----------------------------------------------------------------------------
// Style Configurations
// -----------------------------------------------------------------------------

// Maps intents to their styling based on variant
const styles: Record<BadgeIntent, Record<BadgeVariant, string>> = {
  free: {
    solid: 'bg-slate-200 text-slate-600',
    soft:  'bg-slate-100 text-slate-500 border border-slate-200',
    outline: 'bg-transparent text-slate-500 border border-slate-300',
  },
  core: {
    solid: 'bg-[#1B365D] text-white', // Navy
    soft:  'bg-blue-50 text-[#1B365D] border border-blue-100',
    outline: 'bg-transparent text-[#1B365D] border border-[#1B365D]',
  },
  pro: {
    solid: 'bg-[#C9A227] text-white', // Gold
    soft:  'bg-yellow-50 text-[#9A7D0A] border border-[#C9A227]/30',
    outline: 'bg-transparent text-[#C9A227] border border-[#C9A227]',
  },
  neutral: { // For "Coming Soon"
    solid: 'bg-slate-100 text-slate-500', 
    soft:  'bg-slate-50 text-slate-400 border border-slate-200', 
    outline: 'bg-transparent text-slate-400 border border-slate-300',
  },
  dev: { // For "In Dev"
    solid: 'bg-blue-100 text-[#1B365D]',
    soft:  'bg-blue-50 text-[#1B365D] border border-blue-100',
    outline: 'bg-transparent text-blue-600 border border-blue-200',
  }
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export const FeatureBadge: React.FC<BadgeProps> = ({
  intent = 'free',
  variant = 'solid',
  label,
  showIcon = true,
  animate = false,
  className = '',
  children,
  ...props
}) => {
  
  // Base classes always applied
  const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide shadow-sm transition-all";
  
  // Get specific color classes
  const colorClasses = styles[intent][variant];

  // Determine Icon
  let Icon = null;
  if (showIcon) {
    if (intent === 'pro') Icon = ProIcon;
    if (intent === 'neutral') Icon = ClockIcon;
    if (intent === 'dev' && animate) Icon = PulseDot;
  }

  // Determine Text Content
  let text = label;
  if (!text && !children) {
    // Default labels if none provided
    if (intent === 'free') text = 'Free';
    if (intent === 'core') text = 'Core';
    if (intent === 'pro') text = 'Pro';
    if (intent === 'neutral') text = 'Coming Soon';
    if (intent === 'dev') text = 'In Dev';
  }

  return (
    <span className={`${baseClasses} ${colorClasses} ${className}`} {...props}>
      {Icon && <Icon />}
      {text || children}
    </span>
  );
};

export default FeatureBadge;